import { appSettings } from '@/Shared/appSettings';

import * as mdiIcons from '@mdi/js';

// This function converts a Vuetify-style name (e.g., 'mdi-account')
// into the corresponding PascalCase variable name (e.g., 'mdiAccount').
function getIconPath(iconName) {
    if (!iconName) return null;
    const name = iconName.startsWith('mdi-') ? iconName.slice(4) : iconName;
    const parts = name.split('-');
    const pascalName = 'mdi' + parts.map(part => part.charAt(0).toUpperCase() + part.slice(1)).join('');
    return mdiIcons[pascalName];
}

// Helper function to compute the end position given a start and lines array
function computeEnd(start, lines) {
    const row = start.row + (lines.length - 1);
    let column;
    if (lines.length > 1) {
        column = lines[lines.length - 1].length;
    } else {
        column = start.column + lines[0].length;
    }
    return { row, column };
}

// Helper function to check if two deltas can be merged
// Assumes deltas are consecutive (second applied after first)
function canMerge(delta1, delta2) {
    if (delta1.action !== delta2.action) {
        return false;
    }

    const row1 = delta1.end.row;
    const col1 = delta1.end.column;
    const row2 = delta2.start.row;
    const col2 = delta2.start.column;

    if (delta1.action === 'insert') {
        // For inserts, check if the second starts exactly where the first ends
        return row1 === row2 && col1 === col2;
    } else if (delta1.action === 'remove') {
        // For removes, after the first remove, positions shift left, so the second's start should match the first's start if adjacent
        return delta1.start.row === delta2.start.row && delta1.start.column === delta2.start.column;
    }
    return false;
}

// Helper function to merge two mergeable deltas
function merge(delta1, delta2) {
    const action = delta1.action;
    const combinedText = delta1.lines.join('\n') + delta2.lines.join('\n');
    const combinedLines = combinedText.split('\n');

    // For both insert and remove, the start is the first delta's start
    const start = { row: delta1.start.row, column: delta1.start.column };

    const end = computeEnd(start, combinedLines);

    return {
        start,
        end,
        action,
        lines: combinedLines,
        // Ignore 'id' if present, or add if needed
    };
}

// Main function to combine as many deltas as possible into fewer deltas
// Input: array of delta objects (assumed to be in sequential order)
function mergeDeltas(deltas) {
    if (deltas.length === 0) return [];

    //console.log('mergeDeltas');
    //console.log('from: ' + JSON.stringify(deltas));

    // Clone to avoid mutating originals
    const result = [JSON.parse(JSON.stringify(deltas[0]))];

    for (let i = 1; i < deltas.length; i++) {
        const current = JSON.parse(JSON.stringify(deltas[i]));
        const lastIndex = result.length - 1;
        const last = result[lastIndex];

        if (canMerge(last, current)) {
            // Merge and replace the last in result
            result[lastIndex] = merge(last, current);
        } else {
            // Cannot merge, add as separate
            result.push(current);
        }
    }
    //console.log('  to: ' + JSON.stringify(result));

    return result;
}

/** Lexicographic compare: -1 if a < b, 0 if equal, 1 if a > b */
function compareDocumentPosition(a, b) {
    const ar = a.row ?? a.line ?? 0;
    const br = b.row ?? b.line ?? 0;
    if (ar !== br)
        return ar < br ? -1 : 1;
    const ac = a.column ?? 0;
    const bc = b.column ?? 0;
    if (ac !== bc)
        return ac < bc ? -1 : 1;
    return 0;
}

/** Same ordering as Ace Anchor.$pointsInOrder */
function pointsInOrder(point1, point2, equalPointsInOrder) {
    const r1 = point1.row ?? point1.line ?? 0;
    const r2 = point2.row ?? point2.line ?? 0;
    const c1 = point1.column ?? 0;
    const c2 = point2.column ?? 0;
    const bColIsAfter = equalPointsInOrder ? c1 <= c2 : c1 < c2;
    return r1 < r2 || (r1 === r2 && bColIsAfter);
}

/**
 * Map a document position from before a delta to after the delta is applied.
 * Matches Ace insert rules (Anchor / $getTransformedPoint with insertRight=false).
 * For removes, positions inside the half-open range [delta.start, delta.end) become null (no relocation).
 * @param {{ row?: number, line?: number, column?: number }} pos
 * @param {{ action: string, start: { row: number, column: number }, end: { row: number, column: number }, lines: string[] }} delta
 * @returns {{ row: number, column: number } | null}
 */
function transformDocumentPositionForDelta(pos, delta) {
    if (!delta || (delta.action !== 'insert' && delta.action !== 'remove'))
        return { row: pos.row ?? pos.line ?? 0, column: pos.column ?? 0 };

    const point = { row: pos.row ?? pos.line ?? 0, column: pos.column ?? 0 };
    const deltaIsInsert = delta.action === 'insert';
    const deltaRowShift = (deltaIsInsert ? 1 : -1) * (delta.end.row - delta.start.row);
    const deltaColShift = (deltaIsInsert ? 1 : -1) * (delta.end.column - delta.start.column);
    const deltaStart = delta.start;
    const deltaEnd = deltaIsInsert ? deltaStart : delta.end;
    const moveIfEqual = false;

    if (delta.action === 'remove') {
        const inside =
            pointsInOrder(deltaStart, point, true) && !pointsInOrder(deltaEnd, point, true);
        if (inside)
            return null;
    }

    if (pointsInOrder(point, deltaStart, moveIfEqual))
        return { row: point.row, column: point.column };

    if (pointsInOrder(deltaEnd, point, !moveIfEqual))
        return {
            row: point.row + deltaRowShift,
            column: point.column + (point.row === deltaEnd.row ? deltaColShift : 0),
        };

    if (delta.action === 'remove')
        return null;

    return { row: deltaStart.row, column: deltaStart.column };
}

const mapCache = {
    debugMap: {}
};

/** URL key used by selectStack / debugCallStack for GetDebugMapFromName lookups. */
function debugMapNameUrl(projectName, className, methodName) {
    return `Apps/FlowV2/GetDebugMapFromName?ContainerFullName=${projectName}&ClassName=${className}&MethodName=${methodName}`;
}

/** Cache a FromID map under FlowID and the name URL so Break handlers can resolve it. */
function cacheDebugMap(flowMap, flowId) {
    if (!flowMap)
        return;
    if (flowId)
        mapCache.debugMap[flowId] = flowMap;
    if (flowMap.MethodID)
        mapCache.debugMap[flowMap.MethodID] = flowMap;
    if (flowMap.ContainerFullName && flowMap.ClassName && flowMap.MethodName)
        mapCache.debugMap[debugMapNameUrl(flowMap.ContainerFullName, flowMap.ClassName, flowMap.MethodName)] = flowMap;
}

function frameMatchesDebugMap(frame, flowMap) {
    if (!flowMap || !frame)
        return false;
    // Require project + method identity. Never match by statement Id alone —
    // entry helpers (e.g. GetMethodProject) can collide with FlowV2 ItemMap ids.
    if (frame.ProjectName !== flowMap.ContainerFullName)
        return false;
    if (frame.MethodName !== flowMap.MethodName)
        return false;
    if (frame.ClassName === flowMap.ClassName)
        return true;
    // Stack serializers sometimes use short vs fully-qualified class names
    if (flowMap.ClassName && frame.ClassName && (
        flowMap.ClassName.endsWith('.' + frame.ClassName)
        || frame.ClassName.endsWith('.' + flowMap.ClassName)
    ))
        return true;
    return false;
}

const common = {
    saveGlobalSetting(key, value) {
        const localStorageKey = `${this.storageKey}-${key}`;
        appSettings.LocalStorage(localStorageKey, JSON.stringify(value));
    },
    getGlobalSetting(key, defaultModel) {
        const localStorageKey = `${this.storageKey}-${key}`;
        let settingsRaw = appSettings.LocalStorage(localStorageKey);
        if (settingsRaw === null || settingsRaw === undefined || settingsRaw === '') {
            if (typeof defaultModel === 'function')
                return defaultModel();
            return defaultModel;
        }
        // Values are saved via JSON.stringify — parse booleans/numbers/objects, not only {} / [].
        // (String "false" is truthy, so !!getGlobalSetting(...) wrongly became true for Essentials.)
        if (typeof settingsRaw === 'string') {
            try {
                return JSON.parse(settingsRaw);
            }
            catch {
                return settingsRaw;
            }
        }
        return settingsRaw;
    },
};

function expandObject(v, types) {
    let items = [];

    if (v && v.$type == "Platform.Project.ClassInstance, CompilerCore" && 'Fields' in v) {
        // ClassInstance objects should only show ID and their Fields, not all the internal members.
        items.push({
            name: 'ID',
            type: 'string',
            value: v.ID,
            children: null,
        });
        const fields = expandObject(v.Fields);
        for (let f of fields) items.push(f);
        return items;
    }

    if (Array.isArray(v)) {
        for (let i = 0; i < v.length; i++) {
            let name = `[${i}]`;
            let value = v[i];
            let type = types ? (types[name] ?? typeof value) : typeof value;
            let children = null;
            if (value && typeof value === 'object') {
                children = expandObject(value);
                value = Array.isArray(value) ? '[]' : '{}';
            }
            else if (value === null)
                value = 'null';

            items.push({
                name: name,
                type: type,
                value: value,
                children: children,
            });
        }
    }
    else {
        for (let name of Object.keys(v)) {
            let value = v[name];
            let type = types ? (types[name] ?? typeof value) : typeof value;
            let children = null;
            if (value && typeof value === 'object') {
                children = expandObject(value);
                value = Array.isArray(value) ? '[]' : '{}';
            }
            else if (value === null)
                value = 'null';

            items.push({
                name: name,
                type: type,
                value: value,
                children: children,
            });
        }
    }
    return items;
}

/** Fallback when ListPalette has no Groups entry for a PalettePath. */
const DEFAULT_GROUP_COLOR = '#1971c2';

function parseHexColor(value) {
    if (value == null)
        return null;
    let s = String(value).trim();
    if (!s)
        return null;
    if (s[0] === '#')
        s = s.slice(1);
    if (s.length === 3)
        s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
    if (!/^[0-9a-fA-F]{6}$/.test(s))
        return null;
    return {
        r: parseInt(s.slice(0, 2), 16),
        g: parseInt(s.slice(2, 4), 16),
        b: parseInt(s.slice(4, 6), 16),
        hex: `#${s.toLowerCase()}`,
    };
}

function mixWithWhite(rgb, amount) {
    const t = Math.min(1, Math.max(0, amount));
    const mix = (c) => Math.round(c + (255 - c) * t);
    const r = mix(rgb.r).toString(16).padStart(2, '0');
    const g = mix(rgb.g).toString(16).padStart(2, '0');
    const b = mix(rgb.b).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
}

function extractColorToken(raw) {
    if (raw == null || raw === '')
        return null;
    if (typeof raw === 'string')
        return raw;
    if (typeof raw === 'object')
        return raw.glyph ?? raw.Glyph ?? raw.color ?? raw.Color ?? null;
    return null;
}

/**
 * Palette / canvas accent from a Groups[PalettePath] value (hex string or { Color, well, glyph }).
 * Missing or invalid values use DEFAULT_GROUP_COLOR. `well` is a light tint of the glyph unless provided.
 */
function resolveGroupAccent(raw) {
    const parsed = parseHexColor(extractColorToken(raw)) || parseHexColor(DEFAULT_GROUP_COLOR);
    const glyph = parsed.hex;
    const wellRaw = (raw && typeof raw === 'object') ? (raw.well ?? raw.Well) : null;
    const wellParsed = parseHexColor(wellRaw);
    return {
        well: wellParsed ? wellParsed.hex : mixWithWhite(parsed, 0.82),
        glyph,
    };
}

function applyGroupAccent(element, raw) {
    const accent = resolveGroupAccent(raw);
    if (element?.attr) {
        element.attr('iconWell/fill', accent.well);
        element.attr('icon/fill', accent.glyph);
    }
    return accent;
}


export {
    getIconPath,
    computeEnd,
    canMerge,
    merge,
    mergeDeltas,
    compareDocumentPosition,
    pointsInOrder,
    transformDocumentPositionForDelta,
    mapCache,
    debugMapNameUrl,
    cacheDebugMap,
    frameMatchesDebugMap,
    common,
    expandObject,
    DEFAULT_GROUP_COLOR,
    resolveGroupAccent,
    applyGroupAccent,
};